import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { ConfigVariants } from '@ddt-web/config';
import { WebUserDataConstants } from '@ddt-web/domain';
import { InjectSessionStorage, ObjectUtil, SessionStorageKey } from '@ddt-web/generic-utils';
import { Store } from '@ngrx/store';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isString from 'lodash/isString';
import * as lowerCase from 'lodash/lowerCase';
import * as omit from 'lodash/omit';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { APPLICATION_CONFIG_PATH_TOKEN } from '../constants/application-config.token';
import { contentConfigFetched } from '../constants/config-fetched.actions';
import { ConfigKey } from '../constants/config-key.constants';
import { areDebugSurfacesAllowed } from '../debug-surface.util';
import { LocaleUtilResolverService } from '../locale-service';

const CONFIG_QUERY_PARAM_PREFIX = 'cc-';
const CONFIG_KEY_SEGMENT_PATTERN = /^[A-Za-z0-9_-]+$/;
const UNSAFE_CONFIG_KEY_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);

// TODO chmalcol: Look into making this more generic or extending it to suit other purposes like portfolio tags
@Injectable({
	providedIn: 'root'
})
export class ConfigService {
	public static instance: ConfigService; // this is mainly for the decorator

	@InjectSessionStorage(SessionStorageKey.Config) public sessionConfig: any;

	public configOverrideMode: string;

	private mappings = {};
	private mappings$: Subject<{ [key: string]: any }> = new BehaviorSubject(null);
	private readonly DEFAULT_COUNTRY = 'us';

	constructor(
		@Optional() private store?: Store,
		@Optional() private servicePathProvider?: ServicePathProviderService,
		@Optional() private http?: HttpClient,
		@Optional() private localeUtilResolverService?: LocaleUtilResolverService,
		@Optional() @Inject(APPLICATION_CONFIG_PATH_TOKEN) private applicationConfigPath: string = '',
		@Optional() @Inject('environment') private environment: any = {}
	) {
		ConfigService.instance = this;
		this.setMappings();
	}

	public init(appVariants: (string | ConfigVariants)[]): Promise<{ [key: string]: string }> {
		return this.fetchContentConfig(appVariants)
			.toPromise()
			.then(mappings => {
				this.initialiseConfigFromObj(mappings);
				this.mappings$.next(mappings);
				this.store.dispatch(contentConfigFetched({ contentConfig: mappings }));
				return mappings;
			});
	}

	/**
	 * The hasBeenInitialised function checks whether the config we need has been loaded yet.
	 * It should be used in places where we have no guarantee that the API call would have been completed such as RouteGuards and constructors.
	 * @returns Observable of true if the mappings have been retrieved from the API, of false otherwise - continuing to emit as the mappings are updated
	 */
	public hasBeenInitialised(): Observable<boolean> {
		return this.mappings$.asObservable().pipe(map(value => !!value));
	}

	/**
	 * @returns observable of the mappings that have been retrieved from the API
	 */
	public getDefaultConfigObject(): Observable<any> {
		return this.mappings$.asObservable().pipe(
			map(value => {
				if (value) {
					return { ...this.mappings, ...omit(value, ['@type']) };
				}
			})
		);
	}

	public get<T>(key: ConfigKey, defaultValue: T): T {
		let value = get(this.mappings, key);

		if (this.configOverrideMode && this.mappings['_configOverrideMode']?.[this.configOverrideMode]) {
			value = get(this.mappings, `_configOverrideMode.${this.configOverrideMode}.${key}`, value);
		}

		// transform string boolean values to boolean values - temp to support both version until cleanup is completed
		if (isString(value) && (lowerCase(value) === 'true' || lowerCase(value) === 'false')) {
			value = this.getBoolean(key, defaultValue as unknown as boolean);
		}
		return value ?? defaultValue;
	}

	/** @deprecated use configService.get() instead */
	public getBoolean(key: ConfigKey, defaultValue: boolean): boolean {
		let value = get(this.mappings, key);
		if (value) {
			value = value.toLowerCase();
			if (value === 'true' || value === 'false') {
				return value === 'true';
			} else {
				console.warn('The value for key %s exists but is not a boolean. Returning defaultValue: %s', key, defaultValue);
				return defaultValue;
			}
		} else {
			console.warn('No value exists for key %s. Returning defaultValue: %s', key, defaultValue);
			return defaultValue;
		}
	}

	public fetchContentConfig(variants: string[]): Observable<{ [key: string]: any }> {
		if (!isEmpty(this.applicationConfigPath)) {
			const toolsPath = WebUserDataConstants.CONFIGS + this.applicationConfigPath + WebUserDataConstants.CONFIG_LAYER_CONFIG;
			return this.http.get(this.servicePathProvider.getServicePath('api-gateway') + toolsPath, { params: { variants: variants.join(',') } }).pipe(
				take(1),
				map(jsonResponse => {
					const configVariants: Array<{ [key: string]: string }> = get(jsonResponse, 'resultMap.REPORT', [])
						.map(variantResponse => get(variantResponse, 'data', {}))
						.reverse(); // variants come back in reversed priority from the server so we need to flip the order before flattening
					let configData: { [key: string]: any } = {};
					configVariants.forEach(config => {
						configData = ObjectUtil.extend(configData, config);
					});
					return configData;
				}),
				catchError(error => {
					console.error('Content tags error:', error);
					return of({});
				})
			);
		} else {
			return of({});
		}
	}

	public initialiseConfigFromObj(mappings: any): void {
		// Override via query params
		this.overrideConfigFromQueryParam(mappings);

		// The session config is only ever written by the debug window, so it is honoured under the same policy
		const sessionOverride = areDebugSurfacesAllowed() ? this.sessionConfig : null;
		const isUsableSessionOverride = this.isObjectConfigValue(sessionOverride) && !Array.isArray(sessionOverride);
		const config = isUsableSessionOverride ? sessionOverride : omit(mappings, ['@type']);
		this.mappings = ObjectUtil.extend(this.mappings, config);

		window['AC_CONFIG'] = cloneDeep(this.mappings); // useful for debugging
	}

	/**
	 * You can use ?cc-X=Y (or ?cc-X.Y=Z for a nested flag) to override any config flag, without it having to be known upfront.
	 * Outside production this is unrestricted; in production it is limited to internal users on a beta app.
	 * Prototype-related and non-identifier keys are rejected, a flag whose configured value is an object cannot be
	 * replaced (in the config being merged or in the config already merged), and nested overrides only traverse config
	 * objects that already exist.
	 */
	private overrideConfigFromQueryParam(mappings: any) {
		if (!areDebugSurfacesAllowed()) {
			return;
		}

		const queryParams = new URLSearchParams(location.search);
		for (const qpKey of queryParams.keys()) {
			if (qpKey.startsWith(CONFIG_QUERY_PARAM_PREFIX)) {
				const configKey = qpKey.substring(CONFIG_QUERY_PARAM_PREFIX.length);

				let qpValue: any = queryParams.get(qpKey);
				if (qpValue === 'true' || qpValue === 'false') {
					qpValue = qpValue === 'true';
				} else if (/^[\d\.]+$/.test(qpValue)) {
					qpValue = Number(qpValue);
				}

				this.applyConfigOverride(mappings, configKey, qpValue);
			}
		}
	}

	private applyConfigOverride(mappings: any, configKey: string, value: string | number | boolean): void {
		const segments = configKey.split('.');
		if (segments.some(segment => !this.isOverridableConfigKeySegment(segment))) {
			return;
		}

		let target = mappings;
		for (const segment of segments.slice(0, -1)) {
			const child = Object.prototype.hasOwnProperty.call(target, segment) ? target[segment] : undefined;
			if (!this.isObjectConfigValue(child) || Array.isArray(child)) {
				return;
			}
			target = child;
		}

		const leafSegment = segments[segments.length - 1];
		if (this.isObjectConfigValue(target[leafSegment]) || this.isObjectConfigValue(this.resolveConfigValue(this.mappings, segments))) {
			return;
		}

		Object.defineProperty(target, leafSegment, { value, writable: true, enumerable: true, configurable: true });
	}

	/**
	 * Config is merged from several sources (environment, then remote content), so a flag can be absent from the object
	 * currently being merged while already holding an object value in the merged config.
	 */
	private resolveConfigValue(root: any, segments: string[]): any {
		let current = root;
		for (const segment of segments) {
			if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, segment)) {
				return undefined;
			}
			current = current[segment];
		}
		return current;
	}

	private isObjectConfigValue(value: any): boolean {
		return !!value && typeof value === 'object';
	}

	private isOverridableConfigKeySegment(segment: string): boolean {
		return CONFIG_KEY_SEGMENT_PATTERN.test(segment) && !UNSAFE_CONFIG_KEY_SEGMENTS.has(segment.toLowerCase());
	}

	private setMappings(): void {
		if (!this.localeUtilResolverService) {
			return;
		}

		const locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const configKey = (locale !== this.DEFAULT_COUNTRY) ? `config_${locale}` : 'config';
		this.mappings = this.environment?.[configKey] ?? {};
	}
}
